/**
 * WebSocket hook for Notification events
 * Listens to notification.created events and displays notifications
 */

import React, { useEffect, useCallback } from 'react';
import { useWebSocketStatus } from '@/context/WebSocketContext';
import { notification as antNotification, Modal } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { notificationsKeys } from './useNotifications';
import { useMarkAsSeen } from './useNotifications';
import type { NotificationCreatedEvent } from '@/api/types/notification.types';
import {
  getNotificationIcon,
  getNotificationColor,
  playNotificationSound,
} from '@/utils/notifications';
import { useNavigate } from 'react-router-dom';

interface UseNotificationSocketOptions {
  enabled?: boolean;
  playSound?: boolean;
  onNotificationCreated?: (event: NotificationCreatedEvent) => void;
}

export const useNotificationSocket = (
  options: UseNotificationSocketOptions = {}
) => {
  const { enabled = true, playSound = true, onNotificationCreated } = options;
  const { socket, connected } = useWebSocketStatus();
  const queryClient = useQueryClient();
  const { mutate: markAsSeen } = useMarkAsSeen();
  const navigate = useNavigate();

  const handleNotificationClick = useCallback(
    (notificationId: string, actionUrl?: string | null) => {
      // Mark as read
      markAsSeen(notificationId);

      // Navigate to action URL if provided
      if (actionUrl) {
        navigate(actionUrl);
      }
    },
    [markAsSeen, navigate]
  );

  const displayNotification = useCallback(
    (event: NotificationCreatedEvent) => {
      const { data } = event;
      const Icon = getNotificationIcon(data.category);
      const color = getNotificationColor(data.priority);

      // Play sound if enabled
      if (playSound) {
        if (data.priority === 'Notifications.notification_priority.URGENT') {
          playNotificationSound('urgent.mp3');
        } else if (
          data.priority === 'Notifications.notification_priority.HIGH'
        ) {
          playNotificationSound('chime.mp3');
        } else if (
          data.priority === 'Notifications.notification_priority.NORMAL'
        ) {
          playNotificationSound('ding.mp3');
        }
        // LOW priority is silent
      }

      // Display based on priority
      if (data.priority === 'Notifications.notification_priority.URGENT') {
        // URGENT: Show modal (requires acknowledgment)
        Modal.warning({
          title: data.title,
          content: data.content,
          okText: 'I Understand',
          onOk: () => {
            markAsSeen(data.id);
            if (data.action_url) {
              navigate(data.action_url);
            }
          },
          icon: React.createElement(Icon, { style: { color } }),
        });
      } else if (
        data.priority === 'Notifications.notification_priority.HIGH'
      ) {
        // HIGH: Persistent toast (doesn't auto-dismiss)
        antNotification.warning({
          message: data.title,
          description: data.content,
          duration: 0, // Don't auto-close
          icon: React.createElement(Icon, { style: { color } }),
          onClick: () => handleNotificationClick(data.id, data.action_url),
          style: {
            cursor: 'pointer',
          },
        });
      } else if (
        data.priority === 'Notifications.notification_priority.NORMAL'
      ) {
        // NORMAL: Auto-dismiss toast (5 seconds)
        antNotification.info({
          message: data.title,
          description: data.content,
          duration: 5,
          icon: React.createElement(Icon, { style: { color } }),
          onClick: () => handleNotificationClick(data.id, data.action_url),
          style: {
            cursor: 'pointer',
          },
        });
      }
      // LOW: Silent (no toast, only notification center update)

      // Auto-mark as seen (displayed)
      markAsSeen(data.id);
    },
    [playSound, markAsSeen, navigate, handleNotificationClick]
  );

  const handleNotificationCreated = useCallback(
    (event: NotificationCreatedEvent) => {
      console.log('Notification created:', event);

      // Invalidate React Query cache
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
      queryClient.invalidateQueries({ queryKey: notificationsKeys.count() });

      // Display notification
      displayNotification(event);

      // Call custom handler if provided
      onNotificationCreated?.(event);
    },
    [queryClient, displayNotification, onNotificationCreated]
  );

  useEffect(() => {
    if (!socket || !connected || !enabled) return;

    socket.on('notification.created', handleNotificationCreated);

    return () => {
      socket.off('notification.created', handleNotificationCreated);
    };
  }, [socket, connected, enabled, handleNotificationCreated]);

  return { socket, connected };
};
